import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, FileDown, Filter } from 'lucide-react';
import { useUsuarios } from '../../hooks/useUsuarios';
import { useImportarCliente } from '../../hooks/useImportarCliente';
import { Usuario } from '../../types/usuario';
import { HeaderList } from '../../components/HeaderList';
import { TableList } from '../../components/TableList';
import { Pagination } from '../../components/Pagination';
import { UserMenu } from '../../components/UserMenu';
import { FiltroModal, FiltroUsuario } from '../../components/FiltroModal';
import { Badge, BadgeVariant } from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';

const UsuariosLista: React.FC = () => {
  const [busca, setBusca] = useState('');
  const [filtroModalOpen, setFiltroModalOpen] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState<FiltroUsuario>({
    status: 'todos',
    tipo: 'todos',
    saldoOperador: undefined,
    saldoValor: undefined,
  });
  const { usuarios, listLoading, recarregarUsuarios } = useUsuarios();
  const { importarCliente } = useImportarCliente();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { user } = useAuth();

  // Função para recarregar a página após bloquear/desbloquear
  const recarregarPagina = () => {
    // Usa a função específica para recarregar os dados em vez de recarregar a página inteira
    recarregarUsuarios();
  };

  // Função para aplicar filtros
  const aplicarFiltros = (usuario: Usuario) => {
    // Filtro por status
    if (filtroAtivo.status !== 'todos') {
      const isAtivo = usuario.ativo !== false;
      if (filtroAtivo.status === 'ativo' && !isAtivo) return false;
      if (filtroAtivo.status === 'bloqueado' && isAtivo) return false;
    }

    // Filtro por tipo (usuário / revenda)
    if (filtroAtivo.tipo && filtroAtivo.tipo !== 'todos') {
      if (usuario.tipo !== filtroAtivo.tipo) return false;
    }

    // Filtro por saldo
    if (filtroAtivo.saldoOperador && filtroAtivo.saldoValor !== undefined) {
      const saldo = usuario.saldo || 0;
      switch (filtroAtivo.saldoOperador) {
        case '=':
          if (saldo !== filtroAtivo.saldoValor) return false;
          break;
        case '<':
          if (saldo >= filtroAtivo.saldoValor) return false;
          break;
        case '>':
          if (saldo <= filtroAtivo.saldoValor) return false;
          break;
      }
    }

    return true;
  };

  const usuariosFiltrados = (usuarios || []).filter(usuario => {
    // Filtro de busca (existente)
    const passaBusca = busca === '' || 
      usuario.nome.toLowerCase().includes(busca.toLowerCase()) ||
      usuario.email.toLowerCase().includes(busca.toLowerCase()) ||
      usuario.tipo.toLowerCase().includes(busca.toLowerCase()) ||
      (usuario.nome_revenda && usuario.nome_revenda.toLowerCase().includes(busca.toLowerCase())) ||
      (usuario.ativo !== false && busca.toLowerCase().includes('ativo')) ||
      (usuario.ativo === false && busca.toLowerCase().includes('bloqueado'));

    // Aplica filtros avançados
    const passaFiltros = aplicarFiltros(usuario);

    return passaBusca && passaFiltros;
  });

  // Cálculo da paginação
  const isShowingAll = itemsPerPage === -1;
  const totalPages = isShowingAll ? 1 : Math.ceil(usuariosFiltrados.length / itemsPerPage);
  const indexOfLastItem = isShowingAll ? usuariosFiltrados.length : currentPage * itemsPerPage;
  const indexOfFirstItem = isShowingAll ? 0 : indexOfLastItem - itemsPerPage;
  const currentItems = isShowingAll ? usuariosFiltrados : usuariosFiltrados.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reseta para primeira página quando mudar o número de itens
  };

  const handleAplicarFiltro = (filtros: FiltroUsuario) => {
    setFiltroAtivo(filtros);
    setCurrentPage(1); // Reseta para primeira página quando aplicar filtros
  };

  // Função para remover filtro específico
  const removerFiltroStatus = () => {
    setFiltroAtivo(prev => ({
      ...prev,
      status: 'todos'
    }));
    setCurrentPage(1);
  };

  const removerFiltroTipo = () => {
    setFiltroAtivo(prev => ({
      ...prev,
      tipo: 'todos'
    }));
    setCurrentPage(1);
  };

  const removerFiltroSaldo = () => {
    setFiltroAtivo(prev => ({
      ...prev,
      saldoOperador: undefined,
      saldoValor: undefined
    }));
    setCurrentPage(1);
  };

  // Geração dos badges de filtros ativos
  const gerarFilterBadges = () => {
    const badges = [];

    // Badge para filtro de status
    if (filtroAtivo.status && filtroAtivo.status !== 'todos') {
      const statusLabel = filtroAtivo.status === 'ativo' ? 'Ativo' : 'Bloqueado';
      badges.push({
        id: 'status',
        label: `Status: ${statusLabel}`,
        onRemove: removerFiltroStatus
      });
    }

    // Badge para filtro de tipo
    if (filtroAtivo.tipo && filtroAtivo.tipo !== 'todos') {
      const tipoLabel = filtroAtivo.tipo === 'usuario' ? 'Usuários' :
                        filtroAtivo.tipo === 'revenda' ? 'Revendas' : 'Administradores';
      badges.push({
        id: 'tipo',
        label: `Tipo: ${tipoLabel}`,
        onRemove: removerFiltroTipo
      });
    }

    // Badge para filtro de saldo
    if (filtroAtivo.saldoOperador && filtroAtivo.saldoValor !== undefined) {
      const operadorTexto = filtroAtivo.saldoOperador === '=' ? 'igual a' : 
                           filtroAtivo.saldoOperador === '<' ? 'menor que' : 'maior que';
      badges.push({
        id: 'saldo',
        label: `Saldo ${operadorTexto} ${filtroAtivo.saldoValor}`,
        onRemove: removerFiltroSaldo
      });
    }

    return badges;
  };

  // Função para obter variante do badge baseado no tipo
  const getTipoBadgeVariant = (tipo: string): BadgeVariant => {
    switch (tipo) {
      case 'revenda': return 'blue';
      case 'admin': return 'green';
      case 'novo': return 'red';
      default: return 'yellow';
    }
  };

  // Resetar para primeira página quando houver uma nova busca
  useEffect(() => {
    setCurrentPage(1);
  }, [busca]);

  // Adicionar efeito para recarregar dados quando o componente é montado
  useEffect(() => {
    recarregarUsuarios();
  }, []);

  const columns = [
    { header: 'Nome', accessor: 'nome' as const },
    { header: 'Email', accessor: 'email' as const },
    { 
      header: 'Tipo', 
      accessor: 'tipo' as const,
      render: (usuario: Usuario) => (
        <Badge variant={getTipoBadgeVariant(usuario.tipo)} size="sm">
          {usuario.tipo}
        </Badge>
      )
    },
    ...(user?.tipo === 'admin' ? [{
      header: 'Nome Revenda', 
      accessor: 'nome_revenda' as const,
      render: (usuario: Usuario) => {
        return (
          <span>
            {usuario.nome_revenda || '-'}
          </span>
        );
      }
    }] : []),
    { 
      header: 'Status',
      accessor: 'ativo' as const,
      render: (usuario: Usuario) => {
        const isAtivo = usuario.ativo !== false; // Se ativo for undefined, consideramos como true
        const variant: BadgeVariant = isAtivo ? 'green' : 'red';
        const statusText = isAtivo ? 'Ativo' : 'Bloqueado';
        
        return (
          <Badge variant={variant} size="sm">
            {statusText}
          </Badge>
        );
      }
    },
    { 
      header: 'Saldo',
      accessor: 'saldo' as const,
      render: (usuario: Usuario) => {
        const saldoClass = usuario.saldo === 0 ? 'text-red-600' : 'text-black';
        return (
          <span className={saldoClass}>
            {usuario.saldo !== undefined ? usuario.saldo : 0}
          </span>
        );
      }
    },
    { 
      header: 'Ultimo Acesso',
      accessor: 'ultimo_acesso' as const,
      render: (usuario: Usuario) => {
        return (
          <span>
            {usuario.ultimo_acesso 
              ? new Date(usuario.ultimo_acesso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) 
              : '-'}
          </span>
        );
      }
    },
    { 
      header: 'Última Impressão',
      accessor: 'ultima_impressao' as const,
      render: (usuario: Usuario) => {
        return (
          <span>
            {usuario.ultima_impressao 
              ? new Date(usuario.ultima_impressao).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) 
              : '-'}
          </span>
        );
      }
    },
  ];

  const actions = [
    {
      icon: <Pencil size={20} />,
      onClick: (usuario: Usuario) => navigate(`/usuarios/${usuario.id}`),
      className: "text-blue-600 hover:text-blue-900"
    },
    {
      render: (usuario: Usuario) => <UserMenu usuario={usuario} onAfterAction={recarregarPagina} />,
      onClick: () => {},
      className: "flex items-center justify-center"
    }
  ];

  if (listLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <HeaderList
        title="Usuários"
        buttonLabel="Novo Usuário"
        secondButtonLabel=" "
        secondButtonIcon={<FileDown size={20} />}
        secondButtonClassName="flex items-center justify-center gap-2 w-full sm:w-auto bg-teal-600 hover:bg-teal-700"
        thirdButtonLabel="Filtro"
        thirdButtonIcon={<Filter size={20} />}
        thirdButtonClassName="flex items-center justify-center gap-2 w-full sm:w-auto bg-purple-600 hover:bg-purple-700"
        searchPlaceholder="Buscar por nome, email, tipo ou revenda..."
        searchValue={busca}
        onSearchChange={setBusca}
        onButtonClick={() => navigate('/usuarios/novo')}
        onSecondButtonClick={importarCliente}
        onThirdButtonClick={() => setFiltroModalOpen(true)}
        filterBadges={gerarFilterBadges()}
      />

      <TableList
        data={currentItems}
        columns={columns}
        actions={actions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={usuariosFiltrados.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
      />

      <FiltroModal
        isOpen={filtroModalOpen}
        onClose={() => setFiltroModalOpen(false)}
        onAplicarFiltro={handleAplicarFiltro}
        filtroAtual={filtroAtivo}
      />
    </div>
  );
};

export default UsuariosLista; 